import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'customStartsAt',
    'customEndsAt',
    'endsAtPicker',
    'submit',
    'productPicker',
    'shelfPicker',
    'percentField',
    'amountField',
    'search',
    'option',
    'empty',
    'chips',
    'count',
    'hiddenFields',
    'shelfSearch',
    'shelfOption',
    'shelfEmpty',
    'shelfChips',
    'shelfCount',
    'shelfHiddenFields'
  ]

  connect () {
    this.syncSelectionUi()
    this.syncShelfSelectionUi()
    this.syncSubmitLabel()
    // Nested datetime-picker connects in the same tick; defer so the instance exists.
    requestAnimationFrame(() => this.syncEndsMinDate())
  }

  toggleStartMode (event) {
    if (!this.hasCustomStartsAtTarget) return
    this.customStartsAtTarget.classList.toggle('hidden', event.target.value !== 'schedule')
    this.syncSubmitLabel()
    this.syncEndsMinDate()
  }

  toggleCustom (event) {
    if (!this.hasCustomEndsAtTarget) return
    this.customEndsAtTarget.classList.toggle('hidden', event.target.value !== 'custom')
    if (event.target.value === 'custom') {
      requestAnimationFrame(() => this.syncEndsMinDate())
    }
  }

  syncEndsMinDate () {
    if (!this.hasEndsAtPickerTarget) return

    const picker = this.application.getControllerForElementAndIdentifier(
      this.endsAtPickerTarget,
      'datetime-picker'
    )
    if (!picker?.setMinDate) return

    const minEnd = this.earliestEndDate()
    if (minEnd) picker.setMinDate(minEnd)
  }

  earliestEndDate () {
    const mode =
      this.element.querySelector('input[name="shop_quick_sale[start_mode]"]:checked')?.value || 'now'

    if (mode === 'schedule') {
      const startsAt = this.parsePickerDate(
        this.element.querySelector('#shop_quick_sale_starts_at')?.value
      )
      if (startsAt) {
        // End must be strictly after start (flatpickr minute step is 5).
        return new Date(startsAt.getTime() + 5 * 60 * 1000)
      }
    }

    return new Date()
  }

  parsePickerDate (value) {
    const match = String(value || '')
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/)
    if (!match) return null

    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] || 0),
      Number(match[5] || 0),
      0,
      0
    )
    return Number.isNaN(date.getTime()) ? null : date
  }

  syncSubmitLabel () {
    if (!this.hasSubmitTarget) return
    const mode = this.element.querySelector('input[name="shop_quick_sale[start_mode]"]:checked')?.value || 'now'
    this.submitTarget.value = mode === 'schedule' ? 'Schedule flash sale' : 'Start flash sale'
  }

  toggleDiscountKind () {
    const kind = this.element.querySelector('input[name="shop_quick_sale[discount_kind]"]:checked')?.value || 'percent'
    if (this.hasPercentFieldTarget) this.percentFieldTarget.classList.toggle('hidden', kind !== 'percent')
    if (this.hasAmountFieldTarget) this.amountFieldTarget.classList.toggle('hidden', kind !== 'amount')
  }

  toggleScope () {
    const scope = this.selectedScope()
    if (this.hasProductPickerTarget) {
      this.productPickerTarget.classList.toggle('hidden', scope !== 'products')
    }
    if (this.hasShelfPickerTarget) {
      this.shelfPickerTarget.classList.toggle('hidden', scope !== 'shelves')
    }
    if (scope === 'products') {
      this.filter()
      this.focusSearch()
    }
    if (scope === 'shelves') {
      this.filterShelves()
      this.focusShelfSearch()
    }
  }

  selectedScope () {
    return this.element.querySelector('input[name="shop_quick_sale[apply_scope]"]:checked')?.value || 'all'
  }

  filter () {
    if (!this.hasSearchTarget) return

    const query = this.searchTarget.value.trim().toLowerCase()
    let visible = 0

    this.optionTargets.forEach((option) => {
      const text = (option.dataset.productName || option.textContent || '').toLowerCase()
      const match = !query || text.includes(query)
      option.classList.toggle('hidden', !match)
      if (match) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle('hidden', visible > 0)
    }
  }

  filterShelves () {
    if (!this.hasShelfSearchTarget) return

    const query = this.shelfSearchTarget.value.trim().toLowerCase()
    let visible = 0

    this.shelfOptionTargets.forEach((option) => {
      const text = (option.dataset.shelfName || option.textContent || '').toLowerCase()
      const match = !query || text.includes(query)
      option.classList.toggle('hidden', !match)
      if (match) visible += 1
    })

    if (this.hasShelfEmptyTarget) {
      this.shelfEmptyTarget.classList.toggle('hidden', visible > 0)
    }
  }

  toggleProduct (event) {
    this.toggleRow(event, () => this.syncSelectionUi())
  }

  toggleShelf (event) {
    this.toggleRow(event, () => this.syncShelfSelectionUi())
  }

  toggleRow (event, sync) {
    const option = event.currentTarget
    const checkbox = option.querySelector('input[type="checkbox"]')
    if (!checkbox) return

    if (event.target !== checkbox) {
      event.preventDefault()
      checkbox.checked = !checkbox.checked
    }

    sync()
  }

  removeProduct (event) {
    event.preventDefault()
    const id = String(event.currentTarget.dataset.productId || '')
    const option = this.optionTargets.find((row) => String(row.dataset.productId) === id)
    const checkbox = option?.querySelector('input[type="checkbox"]')
    if (checkbox) checkbox.checked = false
    this.syncSelectionUi()
  }

  removeShelf (event) {
    event.preventDefault()
    const id = String(event.currentTarget.dataset.shelfId || '')
    const option = this.shelfOptionTargets.find((row) => String(row.dataset.shelfId) === id)
    const checkbox = option?.querySelector('input[type="checkbox"]')
    if (checkbox) checkbox.checked = false
    this.syncShelfSelectionUi()
  }

  clearSelection (event) {
    event.preventDefault()
    this.clearChecks(this.optionTargets)
    this.syncSelectionUi()
  }

  clearShelfSelection (event) {
    event.preventDefault()
    this.clearChecks(this.shelfOptionTargets)
    this.syncShelfSelectionUi()
  }

  clearChecks (options) {
    options.forEach((option) => {
      const checkbox = option.querySelector('input[type="checkbox"]')
      if (checkbox) checkbox.checked = false
    })
  }

  focusSearch () {
    if (this.hasSearchTarget) this.searchTarget.focus()
  }

  focusShelfSearch () {
    if (this.hasShelfSearchTarget) this.shelfSearchTarget.focus()
  }

  syncSelectionUi () {
    const selected = this.selectedOptions(this.optionTargets)

    if (this.hasCountTarget) {
      this.countTarget.textContent = selected.length
        ? `${selected.length} selected`
        : 'No products selected'
    }

    if (this.hasChipsTarget) {
      this.chipsTarget.innerHTML = selected.length
        ? selected.map((option) => this.chipHtml(option, 'product')).join('')
        : '<p class="text-xs text-slate-500">Search and tap products to include them in this sale.</p>'
    }

    if (this.hasHiddenFieldsTarget) {
      this.hiddenFieldsTarget.innerHTML = selected
        .map(
          (option) =>
            `<input type="hidden" name="shop_quick_sale[product_ids][]" value="${this.escapeAttr(option.dataset.productId)}">`
        )
        .join('')
    }

    this.paintOptions(this.optionTargets)
  }

  syncShelfSelectionUi () {
    const selected = this.selectedOptions(this.shelfOptionTargets)

    if (this.hasShelfCountTarget) {
      this.shelfCountTarget.textContent = selected.length
        ? `${selected.length} selected`
        : 'No categories selected'
    }

    if (this.hasShelfChipsTarget) {
      this.shelfChipsTarget.innerHTML = selected.length
        ? selected.map((option) => this.chipHtml(option, 'shelf')).join('')
        : '<p class="text-xs text-slate-500">Search and tap categories to include every product in those categories.</p>'
    }

    if (this.hasShelfHiddenFieldsTarget) {
      this.shelfHiddenFieldsTarget.innerHTML = selected
        .map(
          (option) =>
            `<input type="hidden" name="shop_quick_sale[category_ids][]" value="${this.escapeAttr(option.dataset.shelfId)}">`
        )
        .join('')
    }

    this.paintOptions(this.shelfOptionTargets)
  }

  selectedOptions (options) {
    return options.filter((option) => option.querySelector('input[type="checkbox"]')?.checked)
  }

  paintOptions (options) {
    options.forEach((option) => {
      const checked = !!option.querySelector('input[type="checkbox"]')?.checked
      option.classList.toggle('border-shop-300', checked)
      option.classList.toggle('bg-shop-50', checked)
      option.classList.toggle('ring-1', checked)
      option.classList.toggle('ring-shop-200', checked)
      option.setAttribute('aria-pressed', checked ? 'true' : 'false')
    })
  }

  chipHtml (option, kind) {
    const id = this.escapeAttr(kind === 'shelf' ? option.dataset.shelfId : option.dataset.productId)
    const name = this.escapeHtml(
      (kind === 'shelf' ? option.dataset.shelfName : option.dataset.productName) || 'Item'
    )
    const removeAction = kind === 'shelf' ? 'removeShelf' : 'removeProduct'
    const idAttr = kind === 'shelf' ? 'data-shelf-id' : 'data-product-id'

    return `
      <span class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-shop-200 bg-shop-50 px-2.5 py-1 text-xs font-semibold text-shop-900">
        <span class="truncate">${name}</span>
        <button type="button"
                class="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-shop-200/80 text-[10px] font-black text-shop-900 transition hover:bg-shop-300"
                ${idAttr}="${id}"
                data-action="click->quick-sale-form#${removeAction}"
                aria-label="Remove ${name}">
          ×
        </button>
      </span>
    `
  }

  escapeHtml (value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
  }

  escapeAttr (value) {
    return this.escapeHtml(value).replaceAll('`', '&#96;')
  }
}
